import React, { createContext, useContext, useState, useEffect } from 'react';

interface ValueMaskingContextType {
  isValueMasked: boolean;
  toggleValueMasking: () => void;
}

const ValueMaskingContext = createContext<ValueMaskingContextType | undefined>(undefined);

export const ValueMaskingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isValueMasked, setIsValueMasked] = useState<boolean>(false);

  // Sync with localStorage after mount to avoid hydration mismatch
  useEffect(() => {
    const saved = localStorage.getItem('valueMaskingEnabled');
    if (saved) {
      setIsValueMasked(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    // Save to localStorage whenever the state changes
    localStorage.setItem('valueMaskingEnabled', JSON.stringify(isValueMasked));
  }, [isValueMasked]);

  const toggleValueMasking = () => {
    setIsValueMasked(prev => !prev);
  };

  return (
    <ValueMaskingContext.Provider value={{ isValueMasked, toggleValueMasking }}>
      {children}
    </ValueMaskingContext.Provider>
  );
};

export const useValueMasking = (): ValueMaskingContextType => {
  const context = useContext(ValueMaskingContext);
  if (context === undefined) {
    throw new Error('useValueMasking must be used within a ValueMaskingProvider');
  }
  return context;
};
/**
 * Like useValueMasking, but returns false outside a provider instead of
 * throwing. For components that only need to read the flag.
 */
export const useOptionalValueMasking = (): boolean => {
  const context = useContext(ValueMaskingContext);
  return context?.isValueMasked ?? false;
};
